import {useCallback, useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {SetOfficeToastNotification} from '../../contexts/snack-bar/types';
import {useSnackBar} from '../../contexts/snack-bar/context';
import eventEmitter from '../../events/event-emitter';
import {
  UNLOCK_PENDING_SELECTED_AFFILIATION_CHANGE,
  CLEAR_PENDING_SELECTED_AFFILIATION_CHANGE,
  UNLOCK_PENDING_SELECTED_RELATIONSHIP_CHANGE,
  CLEAR_PENDING_SELECTED_RELATIONSHIP_CHANGE,
} from '../../events/event-types';
import {Modal} from '../modal/modal';
import {translations} from '../../i18n/translations';
import {useTranslation} from 'react-i18next';
import './router-prompt.scss';
import {Stack, Typography} from '@mui/material';

/**
 * Router prompt.
 */
export const RouterPrompt = (props: {
  when: boolean;
  onOK: () => boolean;
  onCancel: () => boolean;
  title?: string;
  subtitle: string;
  message: string;
  toastNotification?: SetOfficeToastNotification;
}) => {
  const snackBarContext = useSnackBar();
  const {t} = useTranslation();
  const history = useHistory();
  const [showPrompt, setShowPrompt] = useState(false);
  const [currentPath, setCurrentPath] = useState('');

  // On mount: Block navigation
  useEffect(() => {
    // If enabled
    if (props.when) {
      // Block navigation
      history.block((prompt) => {
        setCurrentPath(prompt.pathname);
        setShowPrompt(true);
        return 'true';
      });
    } else {
      // Unblock navigation
      history.block(() => {});
    }

    return () => {
      // Unblock navigation
      history.block(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, props.when]);

  // On mount: Block Browser reload
  useEffect(() => {
    // Callbacks
    const beforeUnloadListener = (event: any) => {
      // Prevent default event
      event.preventDefault();

      // Set Message
      return (event.returnValue = props.message);
    };

    // If enabled
    if (props.when) {
      // Block
      window.removeEventListener('beforeunload', beforeUnloadListener, {capture: true});
      window.addEventListener('beforeunload', beforeUnloadListener, {capture: true});
    } else {
      // Unblock
      window.removeEventListener('beforeunload', beforeUnloadListener, {capture: true});
    }

    return () => {
      // Unblock navigation
      window.removeEventListener('beforeunload', beforeUnloadListener, {capture: true});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.when]);

  // Callback
  const handleOK = useCallback(async () => {
    // If defined
    if (props.onOK) {
      // Invoke and get result
      const canRoute: boolean = await Promise.resolve(props.onOK());

      // Evaluate result
      if (canRoute) {
        // Unlock any pending selected affiliation/relationship change request because the route will be changed
        eventEmitter.emit(UNLOCK_PENDING_SELECTED_AFFILIATION_CHANGE);
        eventEmitter.emit(UNLOCK_PENDING_SELECTED_RELATIONSHIP_CHANGE);

        // Action performed, execute unblock
        history.block(() => {});

        // Allow navigation to proceed
        history.push(currentPath);

        // Evaluate toast notification
        if (props.toastNotification) {
          // Show it
          snackBarContext.setOfficeToastNotification(props.toastNotification);
        }
      } else {
        // Clear any pending selected affiliation/relationship change request because the route did not change
        eventEmitter.emit(CLEAR_PENDING_SELECTED_AFFILIATION_CHANGE);
        eventEmitter.emit(CLEAR_PENDING_SELECTED_RELATIONSHIP_CHANGE);
      }
    } else {
      // Clear any pending selected affiliation/relationship change request because the route did not change
      eventEmitter.emit(CLEAR_PENDING_SELECTED_AFFILIATION_CHANGE);
      eventEmitter.emit(CLEAR_PENDING_SELECTED_RELATIONSHIP_CHANGE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath, history, props.onOK]);

  // Callback
  const handleCancel = useCallback(async () => {
    // If defined
    if (props.onCancel) {
      // Invoke and get result
      const canRoute: boolean = await Promise.resolve(props.onCancel());

      // Evaluate result
      if (canRoute) {
        // Unlock any pending selected affiliation/relationship change request because the route changed
        eventEmitter.emit(UNLOCK_PENDING_SELECTED_AFFILIATION_CHANGE);
        eventEmitter.emit(UNLOCK_PENDING_SELECTED_RELATIONSHIP_CHANGE);

        // Action performed, execute unblock
        history.block(() => {});

        // Allow navigation to proceed
        history.push(currentPath);

        // Evaluate toast notification
        if (props.toastNotification) {
          // Show it
          snackBarContext.setOfficeToastNotification(props.toastNotification);
        }
      } else {
        // Clear any pending selected affiliation/relationship change request because the route did not change
        eventEmitter.emit(CLEAR_PENDING_SELECTED_AFFILIATION_CHANGE);
        eventEmitter.emit(CLEAR_PENDING_SELECTED_RELATIONSHIP_CHANGE);
      }
    } else {
      // Clear any pending selected affiliation/relationship change request because the route did not change
      eventEmitter.emit(CLEAR_PENDING_SELECTED_AFFILIATION_CHANGE);
      eventEmitter.emit(CLEAR_PENDING_SELECTED_RELATIONSHIP_CHANGE);
    }

    // Hide Prompt
    setShowPrompt(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath, history, props.onCancel]);

  // Default result
  return (
    <Modal
      id="router-prompt"
      open={showPrompt}
      onClose={handleCancel}
      title={props.title}
      actions={[
        {
          label: t(translations.office.generics.back),
          type: 'destructive',
          onClick: handleCancel,
        },
        {
          label: t(translations.office.generics.exit),
          type: 'nondestructive',
          onClick: handleOK,
        },
      ]}
    >
      <Stack className="router-modal-prompt">
        <Typography className="title">{props.subtitle}</Typography>
        <Typography className="subtitle">{props.message}</Typography>
      </Stack>
    </Modal>
  );
};
